import { useState } from 'react';
import { sameEntityId, type BriefQueuedRow } from '@ni/shared';
import { useOrg } from '../../api/app-context';
import { useAnswerDecision } from '../../api/hooks';
import { standingAnswerLine, standingAnswerOf } from '../../api/errors';
import { FACT_TYPE_LABELS, KEPT_JUST_NOW, sourceTierLabel } from '../../lib/copy';
import { factLine, type FactReader } from '@ni/shared';
import { formatDate, joinClauses, plural } from '../../lib/format';
import { ErrorLine } from '../ui/error-line';
import { WorkingButton } from '../ui/working-button';

/**
 * The open questions that have **no row on this brief to sit at**.
 *
 * Every pending non-blocking question the server returns is either rendered
 * inline at its fact (`fact-row.tsx`) or here; the two sets are complements, and
 * that is the point. Home counts these rows on `status = 'pending'` alone, so
 * before this section existed a question whose finding the page could not draw
 * was counted on Home, invisible on the brief, and holding the rewrite shut with
 * "Answer the N open questions first". A deadlock with no surface.
 *
 * Three shapes arrive here:
 *
 *  - a lineage **on another entity's record** — not a pair fact filed under the
 *    counterparty, which the record read unions in and draws inline, but one
 *    with this subject in neither slot: a duplicate person entity minted
 *    mid-run, or an org-principal fact subjected to the org (§7.2c);
 *  - a lineage a later run **left out**, or one a newer version superseded;
 *  - a row that materialized **no fact at all**.
 *
 * It is not behind a disclosure and it is not scoped to a run: an unanswered
 * question is visible on load, and a question that outlived its run is still a
 * question. Deliberately quiet — the same row geometry, the same words and the
 * same endpoint as the inline row, because they are the same question asked
 * about a finding this page happens not to be listing.
 *
 * "Correct" is absent, and only that: correcting edits a fact **on the brief**,
 * which is exactly what these rows do not have.
 */

/** An answered row, kept on screen after the refetch stops returning it. */
interface SettledRow {
  readonly decisionId: string;
  readonly statement: string;
  readonly line: string;
}

/**
 * What the row says it is, in the reader's direction, from whatever it has.
 *
 * `owner` is **who the sentence is written about** — `readerFor` below, and it
 * matters because `factLine`'s whole convention is subject-implicit and
 * counterparty-named: four types branch on whether a party *is* that person, so
 * naming the wrong one flips both the word and the party.
 */
export function statement(row: BriefQueuedRow, owner: FactReader): string {
  if (row.fact_type && row.fields) {
    // The lineage's own current version, rendered the way a fact row renders it
    // — the same sentence the office would have read had the row been inline.
    return factLine(
      {
        fact_type: row.fact_type,
        fields: row.fields,
        /* The row's own columns, so a pair fact reads from the same side it
           would read from inline. `counterparty_name` is already the live,
           tombstone-aware name — the same join the fact projection uses. */
        subject_entity_id: row.fact_subject_id,
        counterparty_entity_id: row.counterparty_entity_id,
        subject_display_name: row.fact_subject_name,
        counterparty_display_name: row.counterparty_name,
      },
      owner,
    );
  }
  // No lineage to read from: the excerpt the question was raised on, which is
  // what the run strip's review card shows for a blocking row.
  if (row.evidence_excerpt) return row.evidence_excerpt;
  // Neither. A type is still more than "a finding".
  return row.fact_type ? FACT_TYPE_LABELS[row.fact_type] : 'Flagged finding';
}

/**
 * Who this row's sentence is written about.
 *
 * This page's subject wherever the fact touches her **at all**, and the fact's
 * own subject only where it does not.
 *
 * Either column counts as touching. The record read unions the counterparty slot
 * (`subject_entity_id = :id OR counterparty_entity_id = :id`), so a pair fact
 * filed under the other person is this subject's fact read from her side — and
 * such a row does reach this section: its lineage left out or superseded (not
 * `active`, so nothing draws it inline), or a sibling question that lost the
 * inline slot to the first row on the lineage. Written from the fact's own
 * subject, that row renders "Married to <this page's subject>" on this page's
 * subject's own brief — her named as her own counterpart, which is the exact
 * render the ids were brought in to end.
 *
 * Foreign is the narrow case this section exists for: a lineage with this
 * subject in **neither** slot — a duplicate person entity minted mid-run, or an
 * org-principal fact subjected to the org. There the page's subject is the wrong
 * party to leave implicit: `factLine` is subject-implicit and counterparty-named,
 * so it would render another person's sibling as this person's, a false statement
 * on a deliverable rather than a display slip.
 *
 * Null `fact_subject_id` means there is no lineage to ask about (or the read
 * could not say), and the honest fallback is this page's own subject — the same
 * assumption every other surface makes about a question raised on this dossier.
 *
 * The **id** goes with the name and is what every branch downstream turns on: the
 * spelling in `fields` is the extractor's, not the entity's.
 */
export function readerFor(row: BriefQueuedRow, subjectId: string, subjectName: string): FactReader {
  /* Folded (`sameEntityId`): both row ids are Postgres's lower-case `uuid` and
     `subjectId` is this dossier's id off the URL, which `ParseUUIDPipe` will
     have accepted whatever case it was typed in. Raw, an upper-cased address
     makes every row look foreign, and the *other* party on the row is handed
     back as the reader — the sentence is then written about them, on this
     subject's own dossier. */
  const onThisSubject =
    sameEntityId(row.fact_subject_id, subjectId) ||
    sameEntityId(row.counterparty_entity_id, subjectId);
  if (row.fact_subject_id !== null && !onThisSubject) {
    return { id: row.fact_subject_id, name: row.fact_subject_name ?? subjectName };
  }
  return { id: subjectId, name: subjectName };
}

/**
 * **Whose record the lineage lives on** — a different question from who the
 * sentence is written about (`readerFor`), and one that has to stay one.
 *
 * A pair fact filed under the counterparty is read from this subject's side
 * *and* keyed to the other person's record: leaving it out negates it where it
 * lives, and the office should not learn that from this page afterwards. So this
 * asks only about the **subject** column, where `readerFor` asks about both.
 *
 * Folded (`sameEntityId`) for the same reason `readerFor` is: `fact_subject_id`
 * is Postgres's lower-case `uuid` while `subjectId` is this dossier's id off the
 * URL, which `ParseUUIDPipe` accepted in whatever case it was typed. Raw, an
 * upper-cased address puts "This finding sits on …'s record" under every row on
 * the reader's own dossier.
 */
export function onAnotherRecord(row: BriefQueuedRow, subjectId: string): boolean {
  return row.fact_subject_id !== null && !sameEntityId(row.fact_subject_id, subjectId);
}

function FlaggedRow({
  row,
  subjectId,
  subjectName,
  runId,
  onSettled,
}: {
  row: BriefQueuedRow;
  /** This dossier's subject **entity id** — what `fact_subject_id` is compared to. */
  subjectId: string;
  subjectName: string;
  /** The live run to refetch alongside the answer, when one is live. */
  runId: string | null;
  onSettled: (settled: SettledRow) => void;
}) {
  const answer = useAnswerDecision(subjectId);
  /* The first-party tier reads in the org's own words. */
  const { vertical } = useOrg();

  const owner = readerFor(row, subjectId, subjectName);

  const filedElsewhere = onAnotherRecord(row, subjectId);

  /* A finding a later run re-raised on a lineage that was already left out
     (§3g look-alikes). The question is real and answering it is what clears it,
     but `keep` on a negated lineage writes nothing — a non-blocking keep has no
     consequence beyond the answer itself, and the substrate's own rule is that
     an attach never resurrects (`facts.service.ts`). Saying so is the difference
     between an honest control and one that quietly does nothing. The superseded
     case is the same courtesy for the other direction: the lineage is live, but
     what the brief is showing is a newer version than the one this asks about. */
  const negated = row.lineage_status === 'negated';
  const superseded = row.lineage_status === 'superseded';

  const srcLine = joinClauses([
    row.counterparty_name,
    row.source_tier ? sourceTierLabel(vertical, row.source_tier) : null,
    row.content_date ? formatDate(row.content_date) : null,
  ]);

  const settle = (line: string) =>
    onSettled({ decisionId: row.decision_id, statement: statement(row, owner), line });

  const answered = (line: string) => ({
    onSuccess: () => settle(line),
    /* Somebody answered first. The 409 carries their answer, and the row is
       about to be refetched away — so it settles in place with their words
       rather than flashing an error at a question that is already resolved. */
    onError: (error: unknown) => {
      const standing = standingAnswerOf(error);
      if (standing) settle(standingAnswerLine(standing));
    },
  });

  return (
    <div className="cat-item queued">
      <div className="cat-line">{statement(row, owner)}</div>

      {srcLine.length > 0 ? <div className="src">{srcLine}</div> : null}

      <div className="queued-note">Flagged for review.</div>

      {/* Whose record is about to change. */}
      {filedElsewhere && row.fact_subject_name ? (
        <div className="src">
          This finding sits on {row.fact_subject_name}’s record. Leaving it out hides it there.
        </div>
      ) : null}

      {negated ? (
        <div className="src">
          This was left out earlier; keeping it will not add it back to the brief.
        </div>
      ) : null}

      {superseded ? (
        <div className="src">
          A newer version of this is on the brief; keeping this one will not change it.
        </div>
      ) : null}

      <div className="queued-actions">
        <WorkingButton
          label="Keep"
          workingLabel="Keeping…"
          tiny
          working={answer.isPending}
          onClick={() =>
            answer.mutate(
              { decisionId: row.decision_id, body: { keep: true }, runId },
              answered(KEPT_JUST_NOW),
            )
          }
        />
        <WorkingButton
          label="Leave out"
          workingLabel="Leaving out…"
          variant="act"
          tiny
          working={answer.isPending}
          onClick={() =>
            answer.mutate(
              { decisionId: row.decision_id, body: { leave_out: true }, runId },
              answered('Kept off the brief · just now'),
            )
          }
        />
      </div>

      {answer.isError && !standingAnswerOf(answer.error) ? (
        <ErrorLine error={answer.error} />
      ) : null}
    </div>
  );
}

export const FLAGGED_FINDINGS_ANCHOR = 'flagged-findings';

export function FlaggedFindings({
  rows,
  subjectId,
  subjectName,
  runId,
}: {
  /** The complement of the inline set — the caller has already partitioned. */
  rows: BriefQueuedRow[];
  subjectId: string;
  subjectName: string;
  runId: string | null;
}) {
  /* An answer takes the row out of the next brief read, which would unmount it —
     and the whole section with it, if it was the last one — before the office
     saw what their click did. Nothing on the brief disappears when it is
     answered (`fact-row.tsx` greys the row and says "Kept · just now"), and this
     section holds to that: the answered row is snapshotted here and goes on
     rendering, settled, for the life of the page. A reload is a fresh visit and
     starts clean, exactly as the inline rows do. */
  const [settled, setSettled] = useState<SettledRow[]>([]);
  const settle = (row: SettledRow) =>
    setSettled((current) =>
      current.some((entry) => entry.decisionId === row.decisionId) ? current : [...current, row],
    );

  const settledIds = new Set(settled.map((entry) => entry.decisionId));
  // Between the answer and the refetch the row is in both lists; the snapshot
  // wins, so it never renders twice.
  const live = rows.filter((row) => !settledIds.has(row.decision_id));

  if (live.length === 0 && settled.length === 0) return null;

  return (
    <div className="section cat" id={FLAGGED_FINDINGS_ANCHOR}>
      <h2>
        <span className="cat-head static">Needs review</span>
        {live.length > 0 ? (
          <span className="tag-faint">{plural(live.length, 'finding', 'findings')}</span>
        ) : null}
      </h2>

      {live.length > 0 ? <div className="src">Not shown on the brief.</div> : null}

      <div className="cat-findings">
        {live.map((row) => (
          <FlaggedRow
            key={row.decision_id}
            row={row}
            subjectId={subjectId}
            subjectName={subjectName}
            runId={runId}
            onSettled={settle}
          />
        ))}

        {settled.map((entry) => (
          <div className="cat-item off" key={entry.decisionId}>
            <div className="cat-line">{entry.statement}</div>
            <div className="src">{entry.line}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
